import { useTeamLearningBudgetMutation } from '@/modules/common/hooks/data-hooks/useTeamLearingBudgetMutation';
import { useUserLearningBudgetMutation } from '@/modules/common/hooks/data-hooks/useUserLearningBudgetMutation';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import {
  LearningBudgetAllocationChange,
  LearningBudgetAllocationModal,
  LearningBudgetTarget,
} from '../components/LearningBudgetAllocationModal';

type UseLearningBudgetManagementOpts = {
  afterUpdate?: () => Promise<unknown>;
};

export function useLearningBudgetManagement(
  opts?: UseLearningBudgetManagementOpts,
) {
  const { afterUpdate } = opts ?? {};

  const [learningBudgetTarget, setLearningBudgetTarget] =
    useState<LearningBudgetTarget | null>(null);

  const { mutateAsync: updateEmployeeLearningBudget } =
    useUserLearningBudgetMutation();

  const { mutateAsync: updateTeamLearningBudget } =
    useTeamLearningBudgetMutation();

  const showModal = (target: LearningBudgetTarget) => {
    setLearningBudgetTarget(target);
  };

  const handleEmployeeLearningBudgetModalClose = useCallback(
    async (learningBudgetChange?: LearningBudgetAllocationChange) => {
      if (!learningBudgetChange || !learningBudgetTarget) {
        setLearningBudgetTarget(null);
        return;
      }

      try {
        if (learningBudgetTarget.variant === 'user') {
          await updateEmployeeLearningBudget({
            user: learningBudgetTarget.user._id,
            amount: learningBudgetChange.allocationOffset,
          });
        } else {
          await updateTeamLearningBudget({
            team: learningBudgetTarget.team._id,
            amount: learningBudgetChange.allocationOffset,
          });
        }
        await afterUpdate?.();
      } catch {
        enqueueSnackbar(
          `Failed to update Learning Budget funds for ${[
            learningBudgetTarget.variant === 'user'
              ? learningBudgetTarget.user.firstName
              : learningBudgetTarget.team.name,
          ]
            .filter(Boolean)
            .join(' ')}. Please try again.`,
          {
            variant: 'error',
          },
        );
      } finally {
        setLearningBudgetTarget(null);
      }
    },
    [
      learningBudgetTarget,
      updateEmployeeLearningBudget,
      updateTeamLearningBudget,
      afterUpdate,
    ],
  );

  const Modal = useMemo(
    () => () => (
      <LearningBudgetAllocationModal
        target={learningBudgetTarget}
        open={!!learningBudgetTarget}
        onClose={handleEmployeeLearningBudgetModalClose}
        isLoading={false}
      />
    ),
    [learningBudgetTarget, handleEmployeeLearningBudgetModalClose],
  );

  return {
    showModal,
    Modal,
  };
}
